import { Check, ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { loadRestaurants, toggleFilterTag } from 'redux/restaurants/actions';
import './FilterMenu.scss';
import { getQueryString } from 'utils/address.util';

const FilterScroller = ({
  filterTags,
  selectedFilterTags,
  filterTagsIcon,
  setSelectedFilterTags,
  loading
}) => {
  const [rightClickCount, setRightClickCount] = useState(0);
  const debounceTimer = useRef(null);
  const [showPopup, setShowPopup] = useState(false);
  const [formDataQuery, setFormDataQuery] = useState(getQueryString());
  const dispatch = useDispatch();
  const [selectedTags, setSelectedTags] = useState([]);
  const scrollRef = useRef(null);
  const toCamelCase = str => {
    return str
      .toLowerCase()
      .replace(/[^a-zA-Z0-9 ]/g, '')
      .trim()
      .replace(/\s+/g, ' ')
      .split(' ')
      .map((word, index) => {
        if (index === 0) return word;
        return word.charAt(0).toUpperCase() + word.slice(1);
      })
      .join('');
  };
  const scroll = direction => {
    if (scrollRef.current) {
      const scrollAmount = 150;
      scrollRef.current.scrollBy({
        left: direction === 'right' ? scrollAmount : -scrollAmount,
        behavior: 'smooth',
      });
      if (direction === 'right') {
        setRightClickCount(prev => prev + 1);
      }
    }
  };

  const handleChange = tag => {
    setSelectedTags(prevTags => {
      const isSelected = prevTags.includes(tag);
      const updatedTags = isSelected
        ? prevTags.filter(t => t !== tag)
        : [...prevTags, tag];
      dispatch(setSelectedFilterTags(updatedTags));
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      // Set new debounce timer
      debounceTimer.current = setTimeout(() => {
        dispatch(
          loadRestaurants({
            queryParams: getQueryString({
              preset_category_names: updatedTags,
            }),
          }),
        );
      }, 1000); // 1 second delay

      return updatedTags;
    });
  };

  useEffect(() => {
    setSelectedTags(selectedFilterTags);
  }, [selectedFilterTags]);

  return (
    <>
      <div className="scroll-slider m-0">
        {filterTags.length > 11 && (
          <button
            onClick={() => scroll('left')}
            className="scroll-arrow scroll-arrow-left"
          >
            <ChevronLeft />
          </button>
        )}

        <div className="slider-inner">
          <div ref={scrollRef} onWheel={e => e.stopPropagation()}>
            {filterTags.slice(0, 20).map((tag, i) => {
              const id = tag.replace(' ', '') + i;
              const isSelected = selectedFilterTags.includes(tag);
              const iconSrc = filterTagsIcon[toCamelCase(tag)];
              return (
                <div key={tag} className="filter-card">
                  <input
                    type="checkbox"
                    id={id}
                    checked={selectedTags.includes(tag)}
                    onChange={() => 
                      handleChange(tag)}
                    style={{ display: 'none' }}
                  />

                  <label key={id} htmlFor={id}>
                    <img src={iconSrc} alt={tag} />
                    <p>{tag}</p>
                    {isSelected && (
                      <div className="checkmark">
                        <Check />
                      </div>
                    )}
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {filterTags.length > 11 && (
          <button
            onClick={() => scroll('right')}
            className="scroll-arrow scroll-arrow-right"
          >
            <ChevronRight />
          </button>
        )}

        <div className="text-center" style={{ position: 'relative' }}>
          <button
            type="button"
            onClick={() => setShowPopup(true)}
            className="btn btn-sm btn-outline-primary px-4"
            style={{ position: 'absolute', right: '0px' }}
          >
            See All Filters
          </button>
        </div>
      </div>
      {showPopup && (
        <div
          className="slider-pop"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <div
            className="slider-content"
            style={{
              backgroundColor: 'white',
              padding: '2rem',
              borderRadius: '12px',
              width: '90%',
            }}
          >
            <h3>Select Cuisine Type(s)</h3>
            <div className="modal-content">
              <button
                // id="modalCloseButton"items
                className="close-button"
                type="button"
                aria-label="Close"
                onClick={() => setShowPopup(false)}
              />
            </div>
            <div
              className="custom-scroll"
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0rem',
                overflowY: 'auto',
                maxHeight: '350px',
                alignContent: 'baseline',
                padding: '15px 0 0',
              }}
            >
              {filterTags.map((tag, i) => {
                const id = tag.replace(' ', '') + i;
                const isSelected = selectedFilterTags.includes(tag);
                const iconSrc = filterTagsIcon[toCamelCase(tag)];
                return (
                  <>
                    <div key={tag} className="filter-card">
                      <input
                        type="checkbox"
                        id={id}
                        checked={isSelected}
                        onChange={() => handleChange(tag)}
                        style={{ display: 'none' }}
                      />
                      <label
                        key={id}
                        htmlFor={id}
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '80px',
                          cursor: 'pointer',
                          justifyContent: 'end',
                          margin: 0,
                          position: 'relative',
                        }}
                      >
                        <img
                          src={iconSrc}
                          alt={tag}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            borderRadius: '100px',
                            transition: 'box-shadow 0.3s ease',
                          }}
                        />
                        <p
                          style={{
                            marginTop: '0.5rem',
                            fontSize: '12px',
                            textTransform: 'capitalize',
                            textAlign: 'center',
                            fontWeight: 500,
                            color: '#282828',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {tag}
                        </p>

                        {/* Checkmark overlay if selected */}
                        {isSelected && (
                          <div
                            style={{
                              position: 'absolute',
                              top: -2,
                              left: 60,
                              width: '16px',
                              height: '16px',
                              backgroundColor: '#299976',
                              color: 'white',
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',

                              fontSize: '16px',
                              fontWeight: 'bold',
                              padding: '2px',
                            }}
                          >
                            <Check />
                          </div>
                        )}
                      </label>
                    </div>
                  </>
                );
              })}

              <div className="d-flex w-100 mt-3 align-items-center justify-content-center">
                <button
                  onClick={() => setShowPopup(false)}
                  className="btn btn-sm btn-outline-primary px-4"
                >
                  View Filtered Results
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilterScroller;
